import React, { useEffect, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { formatDateTime } from "../../utils/formatters";
import { StatusDot } from "../ui/StatusPill";
import { tokens } from "../../theme";

const relative = (ts: number, now: number) => {
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return `${h}h ago`;
};

/** Glass "Live · 40s ago" pill; turns amber when the feed is older than 10 minutes. */
export const LiveIndicator: React.FC<{ lastUpdate?: number; compact?: boolean; inline?: boolean }> = ({
  lastUpdate,
  compact = false,
  inline = false,
}) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(id);
  }, []);

  if (!lastUpdate) return null;
  const stale = now - lastUpdate > 10 * 60 * 1000;

  if (inline) {
    return (
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, whiteSpace: "nowrap" }}>
        <StatusDot tone={stale ? "warning" : "success"} size={6} pulsing={!stale} />
        <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
          {stale ? "Delayed" : "Live"}
        </Box>
        <Box component="span">{relative(lastUpdate, now)}</Box>
      </Box>
    );
  }

  return (
    <Tooltip title={`Last update ${formatDateTime(lastUpdate)}`}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          height: 38,
          px: 1.75,
          borderRadius: 999,
          background: tokens.glass.bg,
          border: `1px solid ${tokens.glass.border}`,
          boxShadow: tokens.glass.highlight,
          backdropFilter: "blur(16px)",
          whiteSpace: "nowrap",
        }}
      >
        <StatusDot tone={stale ? "warning" : "success"} size={7} pulsing={!stale} />
        {!compact && (
          <Typography component="span" sx={{ fontSize: "0.8125rem", fontWeight: 650, color: "text.primary" }}>
            {stale ? "Delayed" : "Live"}
          </Typography>
        )}
        <Typography component="span" sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "text.secondary" }}>
          {relative(lastUpdate, now)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default LiveIndicator;
